import moment from 'moment';
import getNowWithPrepForLocation from 'locations/utils/getNowWithPrep';

import { getCartMinimumSelector } from 'account/reducers';

import { fulfillmentOptions } from 'cart/constants/fulfillmentOptions';
import getAsapLabel, { getAsapLabelRange } from 'base/utils/getAsapLabel';
import { getTime } from 'base/utils/getTime';
import { getWeekday } from 'base/utils/dates';
import { getItemByIdSelector } from 'base/reducers/bentoApi';
import { getNextAvailableTimeFromThrottlesSelector } from 'locations/selectors/locationThrottledTimes';

/* ======= SELECTORS ===== */

/**
 * determine if fulfillment options for asap/later and date and time are set
 * @param {{}} cart - state from redux
 * @return {boolean}
 */
export const areFulfillmentOptionSetSelector = cart =>
  cart.requested_fulfillment_is_asap === true ||
  (cart.requested_fulfillment_is_asap === false &&
    cart.fulfillment_date &&
    cart.fulfillment_time);

/**
 * determine if subtotal is below order min
 * @param {{}} cart - state from redux
 * @return {boolean}
 */
export const isSubtotalBelowMinimumSelector = cart => {
  const orderMinimum = getCartMinimumSelector(cart);
  return cart.subtotal < orderMinimum;
};

/**
 * determine if cart is delivery with no address
 * @param {{}} cart - state from redux
 * @return {boolean}
 */
export const isDeliveryWithNoAddressSelector = cart => {
  return (
    cart.online_ordering_fulfillment_method === fulfillmentOptions.DELIVERY &&
    !cart.shipping_street_address_1
  );
};

/**
 * determine if cart for delivery is valid
 * if it's delivery, is there an address? does not check whether the address is deliverable
 * @param {{}} cart - state from redux
 * @return {boolean}
 */
export const areFulfillmentDeliveryOptionsSetSelector = cart => {
  if (cart.online_ordering_fulfillment_method) {
    return cart.online_ordering_fulfillment_method ===
      fulfillmentOptions.DELIVERY
      ? !!cart.shipping_street_address_1
      : true;
  }
};
/**
 * get the label we use to display next to Asap
 * @param {{}} param
 * @param {{}} param.cart - or cartWorkingCopy state from redux
 * @param {{}} param.restaurantLocations - restaurantLocations state from redux
 * @param {{}} param.locationThrottledTimes - locationThrottledTimes state from redux
 * @return {string} - indicates Asap and estimated fulfillment time (with a range of minutes for delivery and just minutes for asap)
 */
export const getAsapLabelSelector = ({
  cart,
  restaurantLocations,
  locationThrottledTimes
}) => {
  const {
    estimatedAsapDeliveryRange,
    estimatedAsapPickupTime
  } = cartSelectorModule.getThrottledAdjustedAsapEstimate({
    cart,
    restaurantLocations,
    locationThrottledTimes
  });
  return cart.online_ordering_fulfillment_method === fulfillmentOptions.DELIVERY
    ? getAsapLabelRange(estimatedAsapDeliveryRange)
    : getAsapLabel(estimatedAsapPickupTime);
};

/**
 * get estimates that take into account throttles for asap delivery
 * and asap pickup
 * @param {{}} param
 * @param {{}} param.cart - or cartWorkingCopy state from redux
 * @param {{}} param.restaurantLocations - restaurantLocations state from redux
 * @param {{}} param.locationThrottledTimes - locationThrottledTimes state from redux
 * @return {{}} deliveryEstimates - provides delivery and asap
 * @return {number[]} deliveryEstimates.estimatedAsapDeliveryRange
 * - adjusted delivery minutes range (2 member array [min, max] if range exists on cart, otherwise empty array)
 * @return {number} deliveryEstimates.estimatedAsapPickupTime - provides pickup minutes
 */
export const getThrottledAdjustedAsapEstimate = ({
  cart,
  restaurantLocations,
  locationThrottledTimes
}) => {
  const onlineOrderingLocation = cart.online_ordering_location;
  const location = getItemByIdSelector(
    restaurantLocations,
    onlineOrderingLocation
  );
  const nowWithPrep = getNowWithPrepForLocation(location);

  const nextAvailableTime = getNextAvailableTimeFromThrottlesSelector({
    onlineOrderingLocation: cart.online_ordering_location,
    dateTime: nowWithPrep,
    locationThrottledTimes
  });
  const differenceBetweenNextAvailableAndNormalPrep = moment
    .duration(moment(nextAvailableTime).diff(nowWithPrep))
    .get('minutes');
  return {
    estimatedAsapDeliveryRange: (
      cart.online_ordering_estimated_asap_delivery_range || []
    ).map(time => time + differenceBetweenNextAvailableAndNormalPrep),
    estimatedAsapPickupTime:
      cart.online_ordering_estimated_asap_pickup_time +
      differenceBetweenNextAvailableAndNormalPrep
  };
};

/**
 * get the label for later time or asap
 * @param {{}} param
 * @param {{}} param.cart - or cartWorkingCopy state from redux
 * @param {{}} param.restaurantLocations - restaurantLocations state from redux
 * @param {{}} param.locationThrottledTimes - locationThrottledTimes state from redux
 * @return {string} Asap label 'ASAP (30 minutes)` or `ASAP (20 - 30 minutes)`
 */
export const getFulfillmentTimeOrASAPDisplaySelector = ({
  cart,
  restaurantLocations,
  locationThrottledTimes
}) => {
  const { fulfillment_time, fulfillment_date } = cart;

  if (fulfillment_time) {
    const selectedLocation = getItemByIdSelector(
      restaurantLocations,
      cart.online_ordering_location
    );

    const time = getTime(fulfillment_time);
    const day = getWeekday(fulfillment_date);
    const shouldDisplayDay = !moment(fulfillment_date).isSame(
      selectedLocation.today_date
    );
    return `${shouldDisplayDay ? `${day} ` : ''}${time.toLowerCase()}`;
  }

  return cartSelectorModule.getAsapLabelSelector({
    cart,
    restaurantLocations,
    locationThrottledTimes
  });
};

/**
 * get the label for later time or asap
 * @param {{}} cart - cart state from redux
 * @param {number} cartItemId

 * @return {({}|undefined)} cart item or undefined if not found
 */
export const getCartItemByIdSelector = (cart, cartItemId) => {
  return cart.items.find(item => item.id === cartItemId);
};

/**
 * get the label for later time or asap
 * @param {{}} cart - cart state from redux
 * @param {number} productId
 * @return {({}|undefined)} cart item or undefined if not found
 */
export const getCartItemsByProductSelector = (cart, productId) => {
  return cart.items.filter(item => item.product === productId);
};

/**
 * util that returns sum of quantities for all cart items passed in
 * @param {[]} cartItems
 * @return {number}
 */
const getTotalQtyForCartItems = cartItems => {
  return cartItems.reduce((acc, curr) => {
    return acc + curr.quantity;
  }, 0);
};

/**
 * quantity of product in cart (can be spread across multiple cart items)
 * @param {{}} cart - cart state from redux
 * @param {number} productId
 * @return {number}
 */
export const getQuantityOfProductInCart = (cart, productId) => {
  const cartItems = getCartItemsByProductSelector(cart, productId);
  const qtyInCart = getTotalQtyForCartItems(cartItems);

  return qtyInCart;
};

/**
 * total quantity of items in cart
 * @param {{}} cart - cart state from redux
 * @return {number}
 */
export const getTotalCartItemQuantities = cart => {
  return getTotalQtyForCartItems(cart.items);
};

const cartSelectorModule = {
  areFulfillmentOptionSetSelector,
  isSubtotalBelowMinimumSelector,
  isDeliveryWithNoAddressSelector,
  areFulfillmentDeliveryOptionsSetSelector,
  getAsapLabelSelector,
  getThrottledAdjustedAsapEstimate,
  getFulfillmentTimeOrASAPDisplaySelector,
  getCartItemByIdSelector,
  getCartItemsByProductSelector,
  getQuantityOfProductInCart,
  getTotalCartItemQuantities
};

export default cartSelectorModule;
